import { useState } from 'react'

import { pageHasAppliedPageImage, pageShowsCandidatePicker } from '../../lib/comicPageImageStatus'
import {
  COMIC_LAYOUT_GROUPS,
  type ComicPageLayoutTemplate,
  getPageLayoutOption,
  layoutsForGroup,
} from '../../lib/comicPageLayout'
import { readComicPanelDragId } from '../../lib/comicPanelDrag'
import type { ComicPage } from '../../lib/comicTypes'
import { chromeToggleChipClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import { ComicRailChip } from './ComicRailChip'
import {
  ComicRailAddButton,
  ComicRailDeleteButton,
  ComicRailDivider,
  ComicRailSection,
  ComicRailSectionHeader,
  ComicRailViewCandidatesButton,
} from './ComicRailSection'

type Props = {
  angular: boolean
  pages: ComicPage[]
  selectedPageId: string | null
  onSelectPage: (pageId: string) => void
  onAddPage?: () => void
  onDeletePage?: (pageId: string) => void
  onLayoutChange: (layout: ComicPageLayoutTemplate) => void
  draggingPanelId?: string | null
  onPanelDropOnPage?: (pageId: string, panelId: string) => void
  onOpenPageCandidates?: (pageId: string) => void
}

export function ComicPageStrip({
  angular,
  pages,
  selectedPageId,
  onSelectPage,
  onAddPage,
  onDeletePage,
  onLayoutChange,
  draggingPanelId = null,
  onPanelDropOnPage,
  onOpenPageCandidates,
}: Props) {
  const [dropTargetPageId, setDropTargetPageId] = useState<string | null>(null)
  const selected =
    pages.find((p) => p.id === selectedPageId) ?? pages[0] ?? null
  const layout = selected?.layout ?? '2x2'
  const canDeleteSelected =
    onDeletePage != null && pages.length > 1 && selected != null

  const canViewCandidates =
    Boolean(selected && selected.pageCandidates.length > 0 && onOpenPageCandidates)
  const candidatesNeedPick =
    canViewCandidates && selected
      ? pageShowsCandidatePicker(
          selected.pageImageStatus,
          selected.pageCandidates.length,
          Boolean(selected.pageImageUrl),
        )
      : false

  const headerActions =
    onAddPage || canDeleteSelected || canViewCandidates ? (
      <>
        {canViewCandidates && selected ? (
          <ComicRailViewCandidatesButton
            angular={angular}
            label={
              candidatesNeedPick
                ? `选择整页（${selected.pageCandidates.length} 张候选）`
                : `查看整页候选（${selected.pageCandidates.length}）`
            }
            tooltip={
              candidatesNeedPick ? '选择一张应用为本页整页图' : '查看本页已生成的候选图'
            }
            highlight={candidatesNeedPick}
            onClick={() => onOpenPageCandidates!(selected.id)}
          />
        ) : null}
        {canDeleteSelected && selected ? (
          <ComicRailDeleteButton
            angular={angular}
            label={`删除${selected.title}`}
            tooltip="删除当前页"
            onClick={() => onDeletePage(selected.id)}
          />
        ) : null}
        {onAddPage ? (
          <ComicRailAddButton
            angular={angular}
            label="新建页"
            tooltip="新建页"
            onClick={onAddPage}
          />
        ) : null}
      </>
    ) : null

  return (
    <>
      <ComicRailSection>
        <ComicRailSectionHeader title="分页" actions={headerActions} />
        <HorizontalChipStrip
          className="mb-3 min-h-[2.25rem] pb-0.5"
          trackClassName="gap-2"
          role="tablist"
          aria-label="漫画页"
        >
          {pages.map((page) => {
            const active = page.id === selected?.id
            const filled = page.panelIds.length
            const max = getPageLayoutOption(page.layout).maxPanels
            return (
              <ComicRailChip
                key={page.id}
                angular={angular}
                active={active}
                role="tab"
                aria-selected={active}
                title={page.title}
                meta={`(${filled}/${max})`}
                pageImageCommitted={pageHasAppliedPageImage(page)}
                dropHighlight={dropTargetPageId === page.id}
                onClick={() => onSelectPage(page.id)}
                onDragOver={
                  draggingPanelId && onPanelDropOnPage
                    ? (e) => {
                        e.preventDefault()
                        e.dataTransfer.dropEffect = 'move'
                        setDropTargetPageId(page.id)
                      }
                    : undefined
                }
                onDragLeave={
                  onPanelDropOnPage
                    ? () => {
                        setDropTargetPageId((cur) => (cur === page.id ? null : cur))
                      }
                    : undefined
                }
                onDrop={
                  onPanelDropOnPage
                    ? (e) => {
                        e.preventDefault()
                        setDropTargetPageId(null)
                        const panelId = readComicPanelDragId(e.dataTransfer)
                        if (panelId) onPanelDropOnPage(page.id, panelId)
                      }
                    : undefined
                }
              />
            )
          })}
        </HorizontalChipStrip>

        {selected ? (
          <div className="space-y-2.5" aria-label="页内嵌板模板">
            {COMIC_LAYOUT_GROUPS.map((group) => (
              <div key={group.key}>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
                  {group.label}
                </p>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={group.label}>
                  {layoutsForGroup(group.key).map((opt) => (
                    <ChromeTooltip
                      key={opt.value}
                      content={opt.hint}
                      angular={angular}
                      side="top"
                      align="center"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={layout === opt.value}
                        onClick={() => onLayoutChange(opt.value)}
                        className={chromeToggleChipClass(angular, layout === opt.value)}
                      >
                        {opt.label}
                      </button>
                    </ChromeTooltip>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </ComicRailSection>
      <ComicRailDivider />
    </>
  )
}
